<template>
  <b-modal
    :visible="visible"
    size="xl"
    no-close-on-backdrop
    hide-header-close
    body-class="p-2 p-md-3 bg-light"
    footer-class="d-flex justify-content-between align-items-center bg-white p-1"
    centered
    @hidden="$emit('close')"
  >
    <!-- Modal Header Personalizado -->
    <template #modal-header="{ close }">
      <div class="d-flex justify-content-between align-items-center w-100">
        <div class="d-flex align-items-center">
          <div
            class="avatar avatar-lg mr-1"
            :class="colaborador.tipo_personal === 'Driver' ? 'bg-light-warning' : 'bg-light-primary'"
          >
            <feather-icon
              :icon="colaborador.tipo_personal === 'Driver' ? 'TruckIcon' : 'UserIcon'"
              size="24"
              :class="colaborador.tipo_personal === 'Driver' ? 'text-warning' : 'text-primary'"
            />
          </div>
          <div>
            <div class="d-flex align-items-center flex-wrap">
              <h4 class="mb-0 font-weight-bolder text-dark mr-1">
                {{ colaborador.empleado }}
              </h4>
              <b-badge
                :variant="
                  colaborador.estado === 'APROBADO'
                    ? 'light-success'
                    : colaborador.estado === 'GENERADO'
                    ? 'light-info'
                    : 'light-danger'
                "
                class="font-weight-bold px-1 py-50"
              >
                <feather-icon
                  :icon="colaborador.estado === 'APROBADO' ? 'CheckCircleIcon' : 'ClockIcon'"
                  size="12"
                  class="mr-25"
                />
                ESTADO: {{ colaborador.estado }}
              </b-badge>
            </div>
            <div class="d-flex align-items-center flex-wrap font-small-3 text-muted mt-25">
              <span><strong>DNI:</strong> {{ colaborador.numero_documento }}</span>
              <span class="mx-50">•</span>
              <span><strong>Área:</strong> {{ colaborador.area || 'Operaciones' }}</span>
              <span class="mx-50">•</span>
              <span><strong>Régimen:</strong> {{ colaborador.regimen_laboral || 'GENERAL' }}</span>
              <span class="mx-50">•</span>
              <span><strong>Periodo:</strong> {{ periodoInfo && periodoInfo.anio ? periodoInfo.anio : colaborador.anio }} - {{ periodoInfo && periodoInfo.periodo ? periodoInfo.periodo : colaborador.periodo }}</span>
            </div>
          </div>
        </div>

        <b-button
          variant="flat-secondary"
          class="btn-icon rounded-circle p-50"
          @click="close"
          v-b-tooltip.hover.top="'Cerrar'"
        >
          <feather-icon icon="XIcon" size="20" />
        </b-button>
      </div>
    </template>

    <!-- CUERPO DEL MODAL -->
    <div v-if="colaborador" class="cts-detalle-content">
      <!-- Banner Si Ya Está Aprobado -->
      <b-alert
        v-if="colaborador.estado === 'APROBADO'"
        variant="success"
        show
        class="p-1 mb-2 border-0 shadow-sm"
      >
        <div class="d-flex align-items-center">
          <feather-icon icon="CheckCircleIcon" size="22" class="mr-1 text-success flex-shrink-0" />
          <div>
            <strong class="d-block font-medium-1 text-success">CTS APROBADA Y REGISTRADA</strong>
            <span class="font-small-3">
              Esta liquidación semestral de CTS ya fue aprobada y generó su respectiva caja en el módulo de Tesorería.
            </span>
          </div>
        </div>
      </b-alert>

      <!-- Banner Informativo de Periodo Vencido / Interés -->
      <b-alert
        v-if="diasAtrasoCalculados > 0"
        variant="warning"
        show
        class="p-1 mb-2 border-0 shadow-sm"
      >
        <div class="d-flex align-items-start">
          <feather-icon icon="AlertTriangleIcon" size="20" class="mr-1 text-warning flex-shrink-0 mt-25" />
          <div>
            <strong class="text-warning font-medium-1 d-block">Pago Extemporáneo con Interés Laboral Legal</strong>
            <span class="font-small-3">
              La fecha límite legal de depósito fue el <strong>{{ fechaLimiteFormateada }}</strong>. Al haber transcurrido
              <strong>{{ diasAtrasoCalculados }} días de retraso</strong>, la ley laboral peruana exige abonar el interés moratorio
              fijado por la SBS acumulado al depósito neto.
            </span>
          </div>
        </div>
      </b-alert>

      <!-- TARJETAS RESUMEN HERO (KPIs) -->
      <b-row class="mb-2">
        <b-col md="4" cols="12" class="mb-1 mb-md-0">
          <div class="hero-kpi-card p-1 bg-white rounded shadow-sm border-left-primary h-100">
            <div class="d-flex align-items-center justify-content-between mb-50">
              <span class="text-muted font-small-3 font-weight-bold text-uppercase">Tiempo Computable</span>
              <div class="kpi-icon-badge bg-light-primary text-primary">
                <feather-icon icon="CalendarIcon" size="18" />
              </div>
            </div>
            <h3 class="font-weight-bolder text-primary mb-25">
              {{ colaborador.meses_computables || 0 }}m {{ colaborador.dias_computables || 0 }}d
            </h3>
            <span class="font-small-2 text-muted">
              Semestre computado (Nov - Abr)
            </span>
          </div>
        </b-col>

        <b-col md="4" cols="12" class="mb-1 mb-md-0">
          <div class="hero-kpi-card p-1 bg-white rounded shadow-sm border-left-info h-100">
            <div class="d-flex align-items-center justify-content-between mb-50">
              <span class="text-muted font-small-3 font-weight-bold text-uppercase">Remun. Computable</span>
              <div class="kpi-icon-badge bg-light-info text-info">
                <feather-icon icon="DollarSignIcon" size="18" />
              </div>
            </div>
            <h3 class="font-weight-bolder text-info mb-25">
              S/ {{ formatMoney(colaborador.remuneracion_base_cts) }}
            </h3>
            <span class="font-small-2 text-muted">
              Base de cálculo (Sueldo + Conceptos de Ley)
            </span>
          </div>
        </b-col>

        <b-col md="4" cols="12">
          <div class="hero-kpi-card p-1 bg-white rounded shadow-sm border-left-success h-100">
            <div class="d-flex align-items-center justify-content-between mb-50">
              <span class="text-muted font-small-3 font-weight-bold text-uppercase">Neto a Depositar</span>
              <div class="kpi-icon-badge bg-light-success text-success">
                <feather-icon icon="AwardIcon" size="18" />
              </div>
            </div>
            <h3 class="font-weight-bolder text-success mb-25" style="font-size: 1.6rem;">
              S/ {{ formatMoney(colaborador.cts_dep_en) }}
            </h3>
            <span class="font-small-2 text-muted">
              CTS Calculada (S/ {{ formatMoney(colaborador.cts_calculado) }}) + Interés (S/ {{ formatMoney(colaborador.interes_cts) }})
            </span>
          </div>
        </b-col>
      </b-row>

      <!-- PASO 1 Y PASO 2 EN DOS COLUMNAS -->
      <b-row class="mb-2">
        <!-- PASO 1: TIEMPO COMPUTABLE -->
        <b-col lg="6" cols="12" class="mb-1 mb-lg-0">
          <div class="detail-section-card bg-white p-1 rounded shadow-sm h-100">
            <div class="section-title-wrap d-flex align-items-center mb-1">
              <span class="step-badge bg-primary text-white mr-75">PASO 1</span>
              <div>
                <h5 class="mb-0 font-weight-bold text-dark">Tiempo de Servicio Computable</h5>
                <span class="font-small-2 text-muted">Periodo semestral legal y días efectivos laborados</span>
              </div>
            </div>

            <div class="breakdown-list">
              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <div class="d-flex align-items-center">
                  <feather-icon icon="CalendarIcon" size="15" class="text-muted mr-50" />
                  <span class="text-dark font-small-3">Fecha de Ingreso a la Empresa</span>
                </div>
                <span class="font-weight-bold text-dark font-small-3">
                  {{ formatDate(colaborador.fecha_ingreso) }}
                </span>
              </div>

              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <div class="d-flex align-items-center">
                  <feather-icon icon="ClockIcon" size="15" class="text-muted mr-50" />
                  <span class="text-dark font-small-3">Meses Completos Laborados</span>
                </div>
                <span class="font-weight-bold text-primary font-small-3">
                  {{ colaborador.meses_computables || 0 }} meses
                </span>
              </div>

              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <div class="d-flex align-items-center">
                  <feather-icon icon="CheckIcon" size="15" class="text-muted mr-50" />
                  <span class="text-dark font-small-3">Días Fracción Laborados</span>
                </div>
                <span class="font-weight-bold text-primary font-small-3">
                  {{ colaborador.dias_computables || 0 }} días
                </span>
              </div>

              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <div class="d-flex align-items-center">
                  <feather-icon icon="AlertCircleIcon" size="15" class="text-danger mr-50" />
                  <div>
                    <span class="text-dark font-small-3">Inasistencias / Días No Computables</span>
                    <small class="d-block text-muted font-small-1">Descuento de 1/30 por falta (Art. 8 D.L. 650)</small>
                  </div>
                </div>
                <span
                  class="font-weight-bold font-small-3"
                  :class="Number(colaborador.dias_inasistencia) > 0 ? 'text-danger' : 'text-muted'"
                >
                  {{ Number(colaborador.dias_inasistencia) > 0 ? `-${colaborador.dias_inasistencia} día(s)` : '0 días' }}
                </span>
              </div>

              <div class="summary-highlight-row d-flex justify-content-between align-items-center p-1 mt-1 rounded bg-light-primary">
                <strong class="text-primary font-small-3">TOTAL TIEMPO COMPUTADO NETO</strong>
                <span class="badge badge-primary font-medium-1 px-1 py-50">
                  {{ colaborador.meses_computables || 0 }} meses + {{ colaborador.dias_computables || 0 }} días
                </span>
              </div>
            </div>
          </div>
        </b-col>

        <!-- PASO 2: REMUNERACIÓN COMPUTABLE -->
        <b-col lg="6" cols="12">
          <div class="detail-section-card bg-white p-1 rounded shadow-sm h-100">
            <div class="section-title-wrap d-flex align-items-center mb-1">
              <span class="step-badge bg-info text-white mr-75">PASO 2</span>
              <div>
                <h5 class="mb-0 font-weight-bold text-dark">Base de Cálculo (Remun. Computable)</h5>
                <span class="font-small-2 text-muted">Conceptos remunerativos computables según D.L. 650</span>
              </div>
            </div>

            <div class="breakdown-list">
              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <span class="text-dark font-small-3">1. Sueldo Básico Mensual</span>
                <span class="font-weight-bold text-dark font-small-3">
                  S/ {{ formatMoney(colaborador.sueldo_basico) }}
                </span>
              </div>

              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <div>
                  <span class="text-dark font-small-3">2. Asignación Familiar</span>
                  <small class="d-block text-muted font-small-1">10% RMV para trabajadores con hijos</small>
                </div>
                <span class="font-weight-bold text-dark font-small-3">
                  S/ {{ formatMoney(colaborador.asignacion_familiar) }}
                </span>
              </div>

              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <div>
                  <span class="text-dark font-small-3">3. Promedio Horas Extras</span>
                  <small class="d-block text-muted font-small-1">Promedio semestral (regularidad ≥ 3 meses)</small>
                </div>
                <span class="font-weight-bold text-dark font-small-3">
                  S/ {{ formatMoney(colaborador.promedio_horas_extras) }}
                </span>
              </div>

              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <div>
                  <span class="text-dark font-small-3">4. Promedio Bonos / Comisiones</span>
                  <small class="d-block text-muted font-small-1">Promedio semestral de bonificaciones fijas</small>
                </div>
                <span class="font-weight-bold text-dark font-small-3">
                  S/ {{ formatMoney(colaborador.promedio_bono_desempeno) }}
                </span>
              </div>

              <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                <div>
                  <span class="text-dark font-small-3 font-weight-bold">5. Sexto de Gratificación (1/6)</span>
                  <small class="d-block text-info font-small-1">Un sexto de la gratificación percibida (Ley 650)</small>
                </div>
                <span class="font-weight-bold text-info font-small-3">
                  + S/ {{ formatMoney(colaborador.promedio_gratificacion) }}
                </span>
              </div>

              <div class="summary-highlight-row d-flex justify-content-between align-items-center p-1 mt-1 rounded bg-light-info">
                <strong class="text-info font-small-3">TOTAL REMUNERACIÓN COMPUTABLE</strong>
                <span class="badge badge-info font-medium-1 px-1 py-50">
                  S/ {{ formatMoney(colaborador.remuneracion_base_cts) }}
                </span>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- PASO 3 Y PASO 4: FÓRMULA MATEMÁTICA E INTERÉS -->
      <b-row class="mb-2">
        <!-- PASO 3: FÓRMULA LEGAL -->
        <b-col lg="6" cols="12" class="mb-1 mb-lg-0">
          <div class="detail-section-card bg-white p-1 rounded shadow-sm h-100">
            <div class="section-title-wrap d-flex align-items-center mb-1">
              <span class="step-badge bg-success text-white mr-75">PASO 3</span>
              <div>
                <h5 class="mb-0 font-weight-bold text-dark">Fórmula Legal y Cálculo Paso a Paso</h5>
                <span class="font-small-2 text-muted">Cálculo proporcional por meses completos y días laborados</span>
              </div>
            </div>

            <!-- Caja Didáctica de Fórmula Meses -->
            <div class="math-box p-1 mb-1 rounded bg-light border">
              <div class="d-flex justify-content-between align-items-center mb-50">
                <strong class="text-dark font-small-3">A. Cálculo por Meses Completos</strong>
                <span class="badge badge-light-primary">{{ colaborador.meses_computables || 0 }} Meses</span>
              </div>
              <div class="formula-code p-50 bg-white rounded font-small-2 text-monospace border mb-50">
                [Remuneración Computable ÷ 12] × {{ colaborador.meses_computables || 0 }} meses
              </div>
              <div class="d-flex justify-content-between align-items-center font-small-3">
                <span class="text-muted">
                  [S/ {{ formatMoney(colaborador.remuneracion_base_cts) }} ÷ 12] × {{ colaborador.meses_computables || 0 }}
                </span>
                <strong class="text-primary font-medium-1">
                  = S/ {{ formatMoney(calculoMesesMonto) }}
                </strong>
              </div>
            </div>

            <!-- Caja Didáctica de Fórmula Días -->
            <div class="math-box p-1 mb-1 rounded bg-light border">
              <div class="d-flex justify-content-between align-items-center mb-50">
                <strong class="text-dark font-small-3">B. Cálculo por Días Fracción</strong>
                <span class="badge badge-light-secondary">{{ colaborador.dias_computables || 0 }} Días</span>
              </div>
              <div class="formula-code p-50 bg-white rounded font-small-2 text-monospace border mb-50">
                [Remuneración Computable ÷ 360] × {{ colaborador.dias_computables || 0 }} días
              </div>
              <div class="d-flex justify-content-between align-items-center font-small-3">
                <span class="text-muted">
                  [S/ {{ formatMoney(colaborador.remuneracion_base_cts) }} ÷ 360] × {{ colaborador.dias_computables || 0 }}
                </span>
                <strong class="text-secondary font-medium-1">
                  = S/ {{ formatMoney(calculoDiasMonto) }}
                </strong>
              </div>
            </div>

            <div class="summary-highlight-row d-flex justify-content-between align-items-center p-1 rounded bg-light-success">
              <div>
                <strong class="text-success font-small-3 d-block">SUBTOTAL CTS CALCULADA</strong>
                <small class="text-muted font-small-1">Suma: Monto Meses + Monto Días</small>
              </div>
              <span class="badge badge-success font-medium-1 px-1 py-50">
                S/ {{ formatMoney(colaborador.cts_calculado) }}
              </span>
            </div>
          </div>
        </b-col>

        <!-- PASO 4: INTERÉS LABORAL MORATORIO SBS -->
        <b-col lg="6" cols="12">
          <div class="detail-section-card bg-white p-1 rounded shadow-sm h-100">
            <div class="section-title-wrap d-flex align-items-center mb-1">
              <span class="step-badge bg-warning text-white mr-75">PASO 4</span>
              <div>
                <h5 class="mb-0 font-weight-bold text-dark">Interés Laboral Moratorio (SBS)</h5>
                <span class="font-small-2 text-muted">Aplicable por pago posterior a la fecha límite legal</span>
              </div>
            </div>

            <div v-if="diasAtrasoCalculados > 0">
              <div class="breakdown-list">
                <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                  <span class="text-dark font-small-3">Fecha Límite Legal de Pago</span>
                  <span class="font-weight-bold text-dark font-small-3">
                    {{ fechaLimiteFormateada }}
                  </span>
                </div>

                <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                  <span class="text-dark font-small-3">Días Transcurridos en Mora</span>
                  <span class="font-weight-bold text-warning font-small-3">
                    {{ diasAtrasoCalculados }} días de atraso
                  </span>
                </div>

                <div class="breakdown-item d-flex justify-content-between align-items-center py-50 border-bottom">
                  <div>
                    <span class="text-dark font-small-3">Factor Diario Legal SBS</span>
                    <small class="d-block text-muted font-small-1">Tasa legal laboral SBS en moneda nacional</small>
                  </div>
                  <span class="font-weight-bold text-dark font-small-3 text-monospace">
                    0.00005
                  </span>
                </div>

                <!-- Caja Didáctica de Fórmula de Interés -->
                <div class="math-box p-1 my-1 rounded bg-light border">
                  <div class="formula-code p-50 bg-white rounded font-small-2 text-monospace border mb-50">
                    CTS Calculada × Factor Diario SBS × {{ diasAtrasoCalculados }} días
                  </div>
                  <div class="d-flex justify-content-between align-items-center font-small-3">
                    <span class="text-muted">
                      S/ {{ formatMoney(colaborador.cts_calculado) }} × 0.00005 × {{ diasAtrasoCalculados }}
                    </span>
                    <strong class="text-warning font-medium-1">
                      = + S/ {{ formatMoney(colaborador.interes_cts) }}
                    </strong>
                  </div>
                </div>

                <div class="summary-highlight-row d-flex justify-content-between align-items-center p-1 rounded bg-light-warning">
                  <strong class="text-warning font-small-3">TOTAL INTERÉS MORATORIO LABORAL</strong>
                  <span class="badge badge-warning font-medium-1 px-1 py-50">
                    + S/ {{ formatMoney(colaborador.interes_cts) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Caso: Sin Atraso -->
            <div v-else class="text-center py-3">
              <div class="avatar bg-light-success p-1 mb-1 rounded-circle mx-auto">
                <feather-icon icon="CheckCircleIcon" size="28" class="text-success" />
              </div>
              <h5 class="font-weight-bold text-success mb-50">Dentro del Plazo Legal</h5>
              <p class="text-muted font-small-3 mb-0">
                El depósito se encuentra dentro del plazo legal oportuno fijado por ley.
                No genera cobro de intereses moratorios.
              </p>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- CUADRO DE OBSERVACIONES O AVISOS (SI APLICA) -->
      <div v-if="colaborador.motivos && colaborador.motivos.length > 0" class="mb-2">
        <b-card no-body class="border-0 shadow-sm p-1 bg-white">
          <div class="d-flex align-items-center mb-75">
            <feather-icon icon="AlertTriangleIcon" size="18" class="text-warning mr-50" />
            <h5 class="mb-0 font-weight-bold text-dark">Avisos y Observaciones del Cálculo</h5>
          </div>
          <b-alert
            v-for="(motivo, i) in colaborador.motivos"
            :key="i"
            show
            :variant="motivo.tipo === 'error' ? 'danger' : 'warning'"
            class="p-1 mb-50 font-small-3 border-0"
          >
            <div class="d-flex align-items-start">
              <feather-icon
                :icon="motivo.tipo === 'error' ? 'AlertOctagonIcon' : 'AlertTriangleIcon'"
                size="16"
                class="mr-50 mt-25 flex-shrink-0"
              />
              <div>
                <strong class="text-uppercase mr-25">[{{ motivo.tipo }}]:</strong>
                {{ motivo.mensaje }}
              </div>
            </div>
          </b-alert>
        </b-card>
      </div>

      <!-- LIQUIDACIÓN FINAL: TARJETA HERO DE TOTAL A DEPOSITAR -->
      <div class="final-settlement-card p-2 rounded shadow-sm text-center text-md-left d-md-flex align-items-md-center justify-content-between">
        <div class="mb-1 mb-md-0">
          <div class="d-flex align-items-center justify-content-center justify-content-md-start mb-25">
            <span class="badge badge-light-success font-small-2 px-1 py-25 mr-50 text-uppercase font-weight-bolder">
              LIQUIDACIÓN SEMESTRAL FINAL
            </span>
            <span class="text-white-50 font-small-2">
              D.L. 650 Ley de Compensación por Tiempo de Servicios
            </span>
          </div>
          <h2 class="text-white font-weight-bolder mb-25">
            S/ {{ formatMoney(colaborador.cts_dep_en) }}
          </h2>
          <div class="text-white-50 font-small-3">
            Desglose: CTS Calculada <strong>S/ {{ formatMoney(colaborador.cts_calculado) }}</strong>
            <span v-if="Number(colaborador.interes_cts) > 0">
              + Interés Legal Moratorio <strong>S/ {{ formatMoney(colaborador.interes_cts) }}</strong>
            </span>
          </div>
        </div>

        <div class="text-center text-md-right">
          <div class="bg-white rounded p-1 text-dark shadow-sm d-inline-block">
            <span class="font-small-2 text-muted d-block font-weight-bold text-uppercase">Total Neto a Depositar</span>
            <span class="font-medium-3 font-weight-bolder text-success">
              S/ {{ formatMoney(colaborador.cts_dep_en) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- FOOTER DEL MODAL CON ACCIONES CLARAS -->
    <template #modal-footer="{ close }">
      <div class="d-flex align-items-center justify-content-between w-100 flex-wrap">
        <div>
          <!-- Botón Recalcular Individual (solo si GENERADO) -->
          <b-button
            v-if="colaborador && colaborador.estado === 'GENERADO'"
            variant="outline-primary"
            class="mr-1 mb-50 mb-sm-0"
            @click="recalcularIndividual"
          >
            <feather-icon icon="RotateCcwIcon" size="14" class="mr-50" />
            Recalcular Colaborador
          </b-button>
        </div>

        <div class="d-flex align-items-center">
          <!-- Botón Cerrar -->
          <b-button
            variant="outline-secondary"
            class="mr-1"
            @click="close"
          >
            Cerrar
          </b-button>

          <!-- Botón Aprobar Individual (solo si GENERADO) -->
          <b-button
            v-if="colaborador && colaborador.estado === 'GENERADO'"
            variant="success"
            class="font-weight-bolder shadow-sm px-2"
            @click="aprobarIndividual"
          >
            <feather-icon icon="CheckCircleIcon" size="16" class="mr-50" />
            Aprobar CTS (Crear Caja)
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import CtsService from "@/views/rrhh/views/payroll/components/cts/service/cts.service.js";

export default {
  name: "ModalDetalleCts",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    colaborador: {
      type: Object,
      required: true,
    },
    periodoInfo: {
      type: Object,
      default: () => ({}),
    },
    periodoSeleccionado: {
      type: Object,
      default: () => ({}),
    },
    diasAtraso: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
    diasAtrasoCalculados() {
      if (this.diasAtraso > 0) return this.diasAtraso;
      if (!this.periodoInfo || !this.periodoInfo.fecha_limite_deposito) return 0;
      const limite = moment(this.periodoInfo.fecha_limite_deposito);
      const hoy = moment().startOf("day");
      return hoy.isAfter(limite) ? hoy.diff(limite, "days") : 0;
    },
    fechaLimiteFormateada() {
      if (!this.periodoInfo || !this.periodoInfo.fecha_limite_deposito) return "15/05/2026";
      return moment(this.periodoInfo.fecha_limite_deposito).format("DD/MM/YYYY");
    },
    calculoMesesMonto() {
      const base = Number(this.colaborador && this.colaborador.remuneracion_base_cts) || 0;
      const meses = Number(this.colaborador && this.colaborador.meses_computables) || 0;
      return Number(((base / 12) * meses).toFixed(2));
    },
    calculoDiasMonto() {
      const base = Number(this.colaborador && this.colaborador.remuneracion_base_cts) || 0;
      const dias = Number(this.colaborador && this.colaborador.dias_computables) || 0;
      return Number(((base / 360) * dias).toFixed(2));
    },
  },
  methods: {
    formatMoney(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(date) {
      if (!date) return "—";
      return moment(date).format("DD/MM/YYYY");
    },
    async recalcularIndividual() {
      const confirm = await this.showConfirmSwal(
        `¿Recalcular CTS para ${this.colaborador.empleado}?`,
        "Se actualizarán sus días computables, gratificación e ingresos con la información activa.",
        {
          confirmButtonText: "Sí, recalcular",
          cancelButtonText: "Cancelar",
        }
      );
      if (!confirm.isConfirmed) return;

      try {
        this.addPreloader();
        const { data } = await CtsService.generar({
          periodo: this.colaborador.periodo || (this.periodoSeleccionado && this.periodoSeleccionado.periodo),
          anio: this.colaborador.anio || (this.periodoSeleccionado && this.periodoSeleccionado.anio),
          id: this.colaborador.id,
        });

        this.showToastSuccess(data?.msg || data?.message || "Cálculo actualizado correctamente.");
        this.$emit("recalcular", this.colaborador);
      } catch (error) {
        console.error(error);
        this.showToastError(error?.response?.data?.message || error?.message || "Error al recalcular CTS.");
      } finally {
        this.removePreloader();
      }
    },
    async aprobarIndividual() {
      const montoFormateado = this.formatMoney(this.colaborador.cts_dep_en);
      const confirm = await this.showConfirmSwal(
        `¿Aprobar CTS de ${this.colaborador.empleado}?`,
        `Se generará automáticamente la caja en Tesorería por S/ ${montoFormateado} y se marcará como APROBADO.`,
        {
          confirmButtonText: "Sí, aprobar y crear caja",
          cancelButtonText: "Cancelar",
          customClass: {
            confirmButton: "btn btn-outline-success mr-1",
            cancelButton: "btn btn-outline-secondary",
          },
        }
      );
      if (!confirm.isConfirmed) return;

      try {
        this.addPreloader();
        const payload = {
          cts: [{ cts_id: this.colaborador.id }],
          user_id: this.currentUser?.id,
        };

        const { data } = await CtsService.aprobar(payload);

        this.showToastSuccess(data?.msg || data?.message || "CTS aprobada y caja creada en Tesorería exitosamente.");
        this.$emit("aprobado", this.colaborador);
        this.$emit("close");
      } catch (error) {
        console.error(error);
        this.showToastError(error?.response?.data?.msg || error?.response?.data?.message || "Error al aprobar CTS.");
      } finally {
        this.removePreloader();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cts-detalle-content {
  .hero-kpi-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: 1px solid rgba(0, 0, 0, 0.05);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    &.border-left-primary {
      border-left: 4px solid #7367f0 !important;
    }
    &.border-left-info {
      border-left: 4px solid #00cfe8 !important;
    }
    &.border-left-success {
      border-left: 4px solid #28c76f !important;
    }

    .kpi-icon-badge {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .detail-section-card {
    border: 1px solid rgba(0, 0, 0, 0.05);

    .step-badge {
      font-size: 0.75rem;
      font-weight: 800;
      padding: 4px 8px;
      border-radius: 6px;
      letter-spacing: 0.5px;
    }
  }

  .breakdown-item {
    transition: background-color 0.15s ease;
    padding-left: 4px;
    padding-right: 4px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.015);
    }
  }

  .math-box {
    border-radius: 8px;
    .formula-code {
      letter-spacing: 0.2px;
      color: #5e5873;
    }
  }

  .final-settlement-card {
    background: linear-gradient(135deg, #1b3a4b 0%, #065a60 50%, #0f766e 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
}
</style>
